import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Copy,
  Check,
  Network,
  GalleryHorizontal,
  Eye,
  MapPin,
  Palette,
  CalendarDays,
  User2,
  Users,
  Mail,
  Flag,
  Award,
  NotebookPen,
  AtSign,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import {
  STATUS_BUTTON_VARIANTS,
  RATING_BUTTON_VARIANTS,
  toneForOpenCallStatus,
  toneForRating,
  humanize,
  TONE,
} from '../theme/adminVisualSystem';
import {
  StatusBadge,
  RatingBadge,
  ActionButton,
  SectionHeader,
  CurrentStateLine,
} from '../components/VisualAtoms';
import ArtistOnboardingBlock from '../components/ArtistOnboardingBlock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RATINGS = ['average', 'good', 'very_good', 'genius'];
const STATUS_FLOW = ['under_review', 'shortlisted', 'accepted', 'waitlist', 'rejected'];

// Statuses that still need curator review (Previous/Next queue)
const REVIEW_QUEUE_STATUSES = new Set(['new', 'under_review', 'shortlisted', 'waitlist']);

function ensureHttp(url) {
  if (!url) return '';
  return url.startsWith('http') ? url : `https://${url}`;
}

function shortDomain(url) {
  if (!url) return '';
  try {
    const u = url.startsWith('http') ? url : `https://${url}`;
    const parsed = new URL(u);
    return parsed.hostname.replace(/^www\./, '');
  } catch {
    return url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
  }
}

/**
 * Review-material action card.
 * Keeps the URL hidden as a tiny title attribute. The main visual is icon + label + CTA.
 */
function ReviewMaterialCard({ icon: Icon, label, ctaLabel, url, accentTestId }) {
  const [copied, setCopied] = useState(false);
  if (!url) return null;

  const copy = (e) => {
    e.stopPropagation();
    e.preventDefault();
    navigator.clipboard?.writeText(ensureHttp(url)).then(() => {
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 1400);
    });
  };

  return (
    <motion.a
      href={ensureHttp(url)}
      target="_blank"
      rel="noopener noreferrer"
      data-clickable="true"
      data-testid={accentTestId}
      whileHover={{ y: -2 }}
      title={url}
      className="group relative block border border-[#1a1a1a] bg-[#0d0d0d] p-4 hover:border-[#90E0EF]/45 transition-all duration-300"
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 0 0 1px rgba(144,224,239,0.25), 0 0 22px rgba(144,224,239,0.08)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 border border-[#222] flex items-center justify-center text-[#90E0EF] group-hover:bg-[#90E0EF]/10 transition-colors shrink-0">
          <Icon className="w-4 h-4" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-mono text-[#777] tracking-[0.18em] uppercase">{label}</p>
          <p className="font-outfit text-sm font-medium text-white mt-1">{ctaLabel}</p>
          <p className="text-[10px] font-mono text-[#444] mt-0.5 truncate">{shortDomain(url)}</p>
        </div>
        <button
          type="button"
          onClick={copy}
          data-clickable="true"
          aria-label="Copy URL"
          className="w-7 h-7 border border-[#222] flex items-center justify-center text-[#666] hover:text-[#90E0EF] hover:border-[#90E0EF]/40 transition-colors shrink-0"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
      <div className="flex items-center gap-1.5 mt-3 text-[10px] font-mono text-[#90E0EF] tracking-[0.18em] uppercase">
        <span>Open</span>
        <ExternalLink className="w-3 h-3" />
      </div>
    </motion.a>
  );
}

function InfoTile({ icon: Icon, label, value, mono = true }) {
  return (
    <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-3">
      <div className="flex items-center gap-2">
        <Icon className="w-3 h-3 text-[#90E0EF]/80" />
        <p className="text-[10px] font-mono text-[#777] tracking-[0.18em] uppercase">{label}</p>
      </div>
      <p className={`mt-1.5 text-sm ${mono ? 'font-mono' : 'font-outfit'} text-white break-words`}>
        {value || <span className="text-[#444]">—</span>}
      </p>
    </div>
  );
}

export default function OpenCallDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { exhibitionId } = useOutletContext() || {};
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [queue, setQueue] = useState([]); // ids that still need review

  useEffect(() => {
    axios
      .get(`${API}/admin/open-call/${id}`)
      .then((r) => {
        setApp(r.data);
        setNote(r.data.curator_note || '');
      })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [id]);

  // Fetch the review queue (scoped to the current exhibition).
  useEffect(() => {
    const exId = exhibitionId || app?.exhibition_id;
    if (!exId) return;
    axios
      .get(`${API}/admin/open-call?exhibition_id=${encodeURIComponent(exId)}`)
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        // Stable order: created_at ASC (oldest first → curator works through backlog)
        const sorted = [...list].sort((a, b) => {
          const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
          const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
          return ta - tb;
        });
        const ids = sorted
          .filter((row) => REVIEW_QUEUE_STATUSES.has(row.status || 'new'))
          .map((row) => row.id);
        setQueue(ids);
      })
      .catch(() => { /* silent — nav simply disables */ });
  }, [exhibitionId, app?.exhibition_id]);

  const queueInfo = useMemo(() => {
    if (!queue.length) return { idx: -1, total: 0, prevId: null, nextId: null };
    const idx = queue.indexOf(id);
    if (idx === -1) {
      return {
        idx: -1,
        total: queue.length,
        prevId: null,
        nextId: queue[0] || null, // jump to first if current is outside the queue
      };
    }
    return {
      idx,
      total: queue.length,
      prevId: idx > 0 ? queue[idx - 1] : null,
      nextId: idx < queue.length - 1 ? queue[idx + 1] : null,
    };
  }, [queue, id]);

  const patchApp = async (data) => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/admin/open-call/${id}`, data);
      setApp(res.data);
      toast.success('Updated');
    } catch {
      toast.error('Update failed');
    } finally {
      setSaving(false);
    }
  };

  const submittedStr = useMemo(
    () => (app?.created_at ? new Date(app.created_at).toLocaleDateString() : '—'),
    [app],
  );

  if (loading || !app) {
    return <div className="text-[#444] font-mono text-sm p-8">Loading...</div>;
  }

  const attendanceLabel =
    app.venue_attendance === 'yes'
      ? 'Will attend venue'
      : app.venue_attendance === 'maybe'
        ? 'Maybe attending'
        : app.venue_attendance === 'remote'
          ? 'Remote only'
          : app.venue_attendance || '—';

  return (
    <div className="space-y-6" data-testid="open-call-detail">
      <button
        onClick={() => navigate('/admin/open-call')}
        className="flex items-center gap-2 text-xs font-mono text-[#666] hover:text-white transition-colors"
        data-testid="back-to-open-call-list"
      >
        <ArrowLeft className="w-4 h-4" /> Back to applications
      </button>

      {/* Review queue navigation */}
      <div
        className="flex flex-wrap items-center gap-2 sm:gap-3 border border-[#1a1a1a] bg-[#0d0d0d] px-3 sm:px-4 py-2.5"
        data-testid="review-queue-nav"
      >
        <button
          type="button"
          onClick={() => queueInfo.prevId && navigate(`/admin/open-call/${queueInfo.prevId}`)}
          disabled={!queueInfo.prevId}
          data-clickable="true"
          data-testid="review-prev-btn"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[#222] text-[10px] font-mono uppercase tracking-[0.14em] text-[#90E0EF] hover:bg-[#90E0EF]/10 hover:border-[#90E0EF]/45 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-[#222] transition-colors"
          style={{ cursor: !queueInfo.prevId ? 'not-allowed' : 'pointer' }}
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Previous
        </button>
        <button
          type="button"
          onClick={() => queueInfo.nextId && navigate(`/admin/open-call/${queueInfo.nextId}`)}
          disabled={!queueInfo.nextId}
          data-clickable="true"
          data-testid="review-next-btn"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[#222] text-[10px] font-mono uppercase tracking-[0.14em] text-[#90E0EF] hover:bg-[#90E0EF]/10 hover:border-[#90E0EF]/45 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-[#222] transition-colors"
          style={{ cursor: !queueInfo.nextId ? 'not-allowed' : 'pointer' }}
        >
          Next <ChevronRight className="w-3.5 h-3.5" />
        </button>
        <div className="text-[10px] font-mono text-[#666] tracking-[0.14em] ml-1">
          Review queue only
        </div>
        <div className="ml-auto text-[10px] font-mono tabular-nums text-[#888]">
          {queueInfo.idx >= 0
            ? <span><span className="text-white">{queueInfo.idx + 1}</span> / {queueInfo.total} in queue</span>
            : queueInfo.total > 0
              ? <span className="text-[#666]">{queueInfo.total} pending review · this app is outside the queue</span>
              : <span className="text-[#444]">queue empty</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT COLUMN — 2/3 */}
        <div className="lg:col-span-2 space-y-6">
          {/* Artist profile header */}
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="border border-[#1a1a1a] bg-[#0d0d0d] p-6 relative overflow-hidden"
            data-testid="artist-profile-card"
          >
            {/* Corner accents */}
            <span className="absolute top-0 left-0 w-6 h-px bg-[#90E0EF]/40" />
            <span className="absolute top-0 left-0 w-px h-6 bg-[#90E0EF]/40" />
            <span className="absolute top-0 right-0 w-6 h-px bg-[#90E0EF]/40" />
            <span className="absolute top-0 right-0 w-px h-6 bg-[#90E0EF]/40" />

            <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
              {/* Avatar monogram */}
              <div className="w-16 h-16 border border-[#222] bg-[#090909] flex items-center justify-center shrink-0">
                <span className="font-outfit text-xl font-semibold text-[#90E0EF]">
                  {(app.artist_name || '?').trim().charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-mono text-[#90E0EF] tracking-[0.25em] uppercase">
                  ARTIST
                </p>
                <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white tracking-tight mt-1 break-words">
                  {app.artist_name}
                </h1>
                <p className="text-sm font-mono text-[#aaa] mt-1 break-all">{app.email}</p>

                <div className="flex flex-wrap gap-2 mt-4 items-center">
                  <StatusBadge status={app.status} strong />
                  <RatingBadge rating={app.curator_rating} strong />
                  {app.main_medium_style && (
                    <span className="text-[10px] font-mono px-2.5 py-1 border border-[#333] text-[#ccc] uppercase tracking-[0.12em]">
                      {humanize(app.main_medium_style)}
                    </span>
                  )}
                  {app.venue_attendance && (
                    <span className="text-[10px] font-mono px-2.5 py-1 border border-[#333] text-[#ccc] uppercase tracking-[0.12em]">
                      {attendanceLabel}
                    </span>
                  )}
                  <span className="text-[10px] font-mono px-2.5 py-1 border border-[#222] text-[#888] uppercase tracking-[0.12em]">
                    {submittedStr}
                  </span>
                </div>

                {/* Prominent Submitted Work CTA — delicate green */}
                {app.artwork_link && (
                  <motion.a
                    href={ensureHttp(app.artwork_link)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-clickable="true"
                    data-testid="open-submitted-work-top"
                    title={app.artwork_link}
                    whileHover={{ y: -1 }}
                    whileTap={{ scale: 0.98 }}
                    className="group inline-flex items-center gap-3 mt-5 border px-4 py-3 sm:px-5 sm:py-3 transition-all duration-200"
                    style={{
                      color: TONE.green.text,
                      borderColor: TONE.green.border,
                      background: TONE.green.bg,
                      boxShadow: `0 0 18px ${TONE.green.glow}`,
                    }}
                  >
                    <Eye className="w-4 h-4" />
                    <span className="font-outfit text-sm font-medium tracking-tight">
                      Open Submitted Work
                    </span>
                    <ExternalLink className="w-3.5 h-3.5 opacity-80 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </motion.a>
                )}
              </div>
            </div>

            {/* Info tile grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px mt-6">
              <InfoTile icon={Palette} label="Medium" value={humanize(app.main_medium_style)} />
              <InfoTile icon={MapPin} label="Location" value={app.location} />
              <InfoTile icon={Users} label="Attendance" value={attendanceLabel} />
              <InfoTile icon={CalendarDays} label="Submitted" value={submittedStr} />
            </div>
          </motion.section>

          {/* Review materials — designed cards (no raw URL dominance) */}
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.1 }}
          >
            <div className="flex items-baseline justify-between mb-3">
              <SectionHeader
                title="Review Materials"
                helper="Open the artist's references in a new tab."
              />
              <span className="text-[10px] font-mono text-[#555]">// opens in new tab</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="artist-link-cards">
              <ReviewMaterialCard
                icon={Eye}
                label="Submitted Work"
                ctaLabel="Open Submitted Work"
                url={app.artwork_link}
                accentTestId="link-card-artwork"
              />
              <ReviewMaterialCard
                icon={GalleryHorizontal}
                label="Portfolio"
                ctaLabel="Open Portfolio"
                url={app.portfolio_link}
                accentTestId="link-card-portfolio"
              />
              <ReviewMaterialCard
                icon={Network}
                label="Social"
                ctaLabel="Open Social Profile"
                url={app.main_social_media}
                accentTestId="link-card-social"
              />
            </div>
          </motion.section>

          {/* Bio */}
          {app.artist_bio && (
            <motion.section
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.15 }}
              className="border border-[#1a1a1a] bg-[#0d0d0d] p-6"
              data-testid="artist-bio-card"
            >
              <SectionHeader
                icon={User2}
                title="Artist Bio"
                helper="Background, exhibitions, professional experience."
              />
              <p className="text-sm font-mono text-[#d6d6d6] leading-relaxed whitespace-pre-wrap mt-3">
                {app.artist_bio}
              </p>
            </motion.section>
          )}

          {/* Statement */}
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.2 }}
            className="border border-[#1a1a1a] bg-[#0d0d0d] p-6"
            data-testid="artist-statement-card"
          >
            <SectionHeader
              icon={Palette}
              title="Artist Statement"
              helper="Their practice, themes, and connection to 'This Is Humanity'."
            />
            <p className="text-sm font-mono text-[#d6d6d6] leading-relaxed whitespace-pre-wrap mt-3">
              {app.artist_statement || <span className="text-[#444]">// not provided</span>}
            </p>
          </motion.section>
        </div>

        {/* RIGHT COLUMN — sticky curator panel */}
        <aside className="lg:col-span-1">
          <div className="space-y-4 lg:sticky lg:top-4" data-testid="curator-panel">
            {/* Status */}
            <div className="border border-[#1a1a1a] bg-[#0d0d0d] p-5">
              <SectionHeader
                icon={Flag}
                title="Status"
                helper="Control the current review stage of this submission."
              />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {STATUS_FLOW.map((s) => (
                  <ActionButton
                    key={s}
                    variant={STATUS_BUTTON_VARIANTS[s]}
                    active={app.status === s}
                    disabled={saving}
                    onClick={() => patchApp({ status: s })}
                    dense
                    data-testid={`status-${s}`}
                  >
                    {humanize(s)}
                  </ActionButton>
                ))}
              </div>
              <CurrentStateLine
                label="Current status"
                value={app.status || 'new'}
                tone={toneForOpenCallStatus(app.status || 'new')}
              />
            </div>

            {/* Artist Onboarding (always visible: convert / accept+convert / send intake) */}
            <ArtistOnboardingBlock
              app={app}
              exhibitionId={app.exhibition_id}
              onAppUpdate={(merged) => setApp(merged)}
            />

            {/* Rating */}
            <div className="border border-[#1a1a1a] bg-[#0d0d0d] p-5">
              <SectionHeader
                icon={Award}
                title="Curator Rating"
                helper="Grade the long-term value of this artist."
              />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {RATINGS.map((r) => (
                  <ActionButton
                    key={r}
                    variant={RATING_BUTTON_VARIANTS[r]}
                    active={app.curator_rating === r}
                    disabled={saving}
                    onClick={() => patchApp({ curator_rating: r })}
                    dense
                    data-testid={`rating-${r}`}
                  >
                    {humanize(r)}
                  </ActionButton>
                ))}
              </div>
              <CurrentStateLine
                label="Current rating"
                value={app.curator_rating}
                tone={toneForRating(app.curator_rating)}
                emptyLabel="Unrated"
              />
            </div>

            {/* Note */}
            <div className="border border-[#1a1a1a] bg-[#0d0d0d] p-5">
              <SectionHeader
                icon={NotebookPen}
                title="Curator Note"
                helper="Private internal notes visible only to curators."
              />
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={4}
                placeholder="Internal note — only visible to curators…"
                data-testid="curator-note-input"
                className="w-full bg-transparent border border-[#222] text-white font-mono text-xs p-2.5 resize-none focus:outline-none focus:border-[#90E0EF] placeholder:text-[#444] mt-1"
              />
              <div className="mt-2">
                <ActionButton
                  variant="primary"
                  disabled={saving}
                  onClick={() => patchApp({ curator_note: note })}
                  icon={NotebookPen}
                  data-testid="save-note-btn"
                >
                  Save Note
                </ActionButton>
              </div>
            </div>

            {/* Contact */}
            <div className="border border-[#1a1a1a] bg-[#0d0d0d] p-5">
              <SectionHeader
                icon={AtSign}
                title="Contact"
                helper="Applicant communication details."
              />
              <a
                href={`mailto:${app.email}`}
                data-clickable="true"
                className="flex items-center gap-2 text-sm font-mono text-[#90E0EF] hover:text-white transition-colors break-all mt-1"
              >
                <Mail className="w-3.5 h-3.5 shrink-0" />
                {app.email}
              </a>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
